import React, { Dispatch } from 'react';
import { Item, Trigger } from '@radix-ui/react-accordion';
import { StyledContent, StyledRoot } from './SideNavMenu.styles';
import { useSideDrawer } from '../../HBSideDrawer/HBSideDrawer';
import { ISideNavItem } from '../HBSideNav';
import SideNavMenuItemContainer from '../SideNavMenuItem/SideNavMenuItemContainer';
import HBDivider from '../../HBDivider/HBDivider';
import { useSideNavContext } from '../context/SideNavProvider';

interface ISideNavMenuContainer {
    menuItems: ISideNavItem[];
    selectedItem: string | null;
    setSelectedItem: React.Dispatch<React.SetStateAction<string | null>>;
    selectedSubItem: string | null;
    openItem: string | null;
    handleItemClick: (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        details: {
            item: ISideNavItem;
            parentLabel?: ISideNavItem['label'];
            source?: 'sidenav' | 'popover';
        }
    ) => void;
    persistentItemOpen?: ISideNavItem;
    setPersistentItemOpen?: Dispatch<ISideNavItem>;
}

function SideNavMenuContainer({
    menuItems,
    selectedItem,
    setSelectedItem,
    selectedSubItem,
    openItem,
    handleItemClick,
    persistentItemOpen,
    setPersistentItemOpen
}: ISideNavMenuContainer) {
    const { isExpanded, isMinimized, isHovered } = useSideDrawer();
    const { theme } = useSideNavContext();

    return (
        <StyledRoot
            type="single"
            collapsible
            value={selectedItem}
            onValueChange={setSelectedItem}
        >
            {menuItems.map(({ label, subItems, ...menuItem }, index) => {
                const isCurrentlyExpanded = openItem === label;

                const expandAccordionWhenExpanded =
                    isExpanded && isCurrentlyExpanded;

                const expandAccordionWhenHovered =
                    isHovered && !isMinimized && isCurrentlyExpanded;

                const expandValue =
                    expandAccordionWhenExpanded || expandAccordionWhenHovered
                        ? label
                        : null;

                const isItemSelected = selectedItem === label;

                return (
                    <Item key={index} value={expandValue} asChild>
                        <>
                            <Trigger asChild>
                                {menuItem.openOnClick ||
                                subItems?.length === 0 ? (
                                    <>
                                        <HBDivider
                                            margin="8px 0"
                                            color={theme.dividerColor}
                                        />
                                        <SideNavMenuItemContainer
                                            menuItem={{
                                                label,
                                                subItems,
                                                ...menuItem
                                            }}
                                            isSelected={
                                                subItems?.some(
                                                    subItem =>
                                                        subItem.label ===
                                                            selectedSubItem &&
                                                        isItemSelected
                                                ) || isItemSelected
                                            }
                                            onMenuItemClick={handleItemClick}
                                            persistentItemOpen={
                                                persistentItemOpen
                                            }
                                            setPersistentItemOpen={
                                                setPersistentItemOpen
                                            }
                                            source="sidenav"
                                        />
                                    </>
                                ) : (
                                    <SideNavMenuItemContainer
                                        menuItem={{
                                            label,
                                            subItems,
                                            ...menuItem
                                        }}
                                        isOpen={openItem === label}
                                        showChevron={!!subItems}
                                        onMenuItemClick={handleItemClick}
                                        isSelected={
                                            subItems?.some(
                                                subItem =>
                                                    subItem.label ===
                                                        selectedSubItem &&
                                                    isItemSelected
                                            ) || isItemSelected
                                        }
                                        persistentItemOpen={persistentItemOpen}
                                        setPersistentItemOpen={
                                            setPersistentItemOpen
                                        }
                                        source="sidenav"
                                    />
                                )}
                            </Trigger>
                            {subItems && !menuItem.openOnClick && (
                                <StyledContent>
                                    {subItems?.map(
                                        (
                                            {
                                                label: subItemLabel,
                                                ...menuSubItem
                                            },
                                            subItemIndex
                                        ) => (
                                            <SideNavMenuItemContainer
                                                key={subItemIndex}
                                                menuItem={{
                                                    label: subItemLabel,
                                                    ...menuSubItem
                                                }}
                                                showChevron={false}
                                                parentLabel={label}
                                                selectedSubItem={
                                                    selectedSubItem
                                                }
                                                isSelected={
                                                    selectedSubItem ===
                                                        subItemLabel &&
                                                    label === selectedItem
                                                }
                                                onMenuItemClick={
                                                    handleItemClick
                                                }
                                                source="sidenav"
                                            />
                                        )
                                    )}
                                </StyledContent>
                            )}
                        </>
                    </Item>
                );
            })}
        </StyledRoot>
    );
}

export default SideNavMenuContainer;
